// SMS Queue Status Widget
// Shows real-time SMS queue statistics on the dashboard

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageSquare, Clock, CheckCircle, XCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SMSQueueStatus = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    try {
      // Auth via httpOnly cookie - no header needed
      const response = await axios.get(`${API}/api/sms-queue/stats`);
      setStats(response.data);
      setError(null);
    } catch {
      setError('Unable to load SMS queue status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-24">
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="text-center text-slate-500">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
            <p className="text-sm">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const queueHealth = stats?.queue?.total_in_queue === 0 ? 'good' : 
                      stats?.queue?.total_in_queue < 10 ? 'normal' : 
                      stats?.queue?.total_in_queue < 50 ? 'busy' : 'overloaded';

  const healthColors = {
    good: 'bg-green-100 text-green-700 border-green-200',
    normal: 'bg-blue-100 text-blue-700 border-blue-200',
    busy: 'bg-amber-100 text-amber-700 border-amber-200',
    overloaded: 'bg-red-100 text-red-700 border-red-200'
  };

  const healthLabels = {
    good: 'All Clear',
    normal: 'Processing',
    busy: 'Busy',
    overloaded: 'High Volume'
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-teal-500" />
            SMS Queue Status
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchStats}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Queue Health Badge */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Queue Health</span>
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${healthColors[queueHealth]}`}>
            {healthLabels[queueHealth]}
          </span>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* In Queue */}
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-slate-400" />
              <span className="text-xs text-slate-500">In Queue</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {stats?.queue?.total_in_queue || 0}
            </p>
          </div>

          {/* Sent Today */}
          <div className="bg-green-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-xs text-slate-500">Sent Today</span>
            </div>
            <p className="text-2xl font-bold text-green-700">
              {stats?.today?.sent || 0}
            </p>
          </div>

          {/* Failed Today */}
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-4 h-4 text-red-400" />
              <span className="text-xs text-slate-500">Failed Today</span>
            </div>
            <p className={`text-2xl font-bold ${stats?.today?.failed > 0 ? 'text-red-600' : 'text-slate-400'}`}>
              {stats?.today?.failed || 0}
            </p>
          </div>

          {/* Rate Limit */}
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="w-4 h-4 text-slate-400" />
              <span className="text-xs text-slate-500">Rate Limit</span>
            </div>
            <p className="text-lg font-bold text-slate-700">
              {stats?.rate_limit?.messages_per_second || 1}/sec
            </p>
          </div>
        </div>

        {/* Trust Score Note */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs text-amber-700">
            <strong>Trust Score: {stats?.rate_limit?.trust_score || 16}/100</strong>
            <br />
            Messages are queued and sent at {stats?.rate_limit?.messages_per_second || 1} msg/sec to comply with A2P 10DLC limits.
          </p>
        </div>

        {/* Last Sent */}
        {stats?.last_sent_at && (
          <p className="text-xs text-slate-400 text-center">
            Last message sent: {new Date(stats.last_sent_at).toLocaleTimeString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SMSQueueStatus;
